import { useEffect, useState } from 'react';
import { Plus, X } from '@/lib/icons';
import type { FieldDef } from '@/types/entity';
import { EditableText } from './EditableText';
import { TagInput } from './TagInput';
import { EntityPicker } from './EntityPicker';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useCodexStore } from '@/stores/codexStore';
import { entityColor, ENTITY_META } from '@/config/entityMeta';

export function FieldRow({
  field,
  value,
  onChange,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  return (
    <div className="group/field">
      <div className="field-key mb-1">{field.label}</div>
      <FieldEditor field={field} value={value} onChange={onChange} />
    </div>
  );
}

function FieldEditor({
  field,
  value,
  onChange,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  switch (field.type) {
    case 'select':
      return (
        <Select value={value ? String(value) : undefined} onValueChange={onChange}>
          <SelectTrigger className="h-8">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'number':
      return <NumberField value={value} onChange={onChange} />;
    case 'tags':
      return <TagInput tags={(value as string[]) ?? []} onChange={onChange} />;
    case 'entityref':
      return <EntityRefField value={value as string} onChange={onChange} />;
    case 'multiselect':
      return <EntityMultiField value={(value as string[]) ?? []} onChange={onChange} />;
    default:
      return (
        <EditableText
          value={value == null ? '' : String(value)}
          multiline={field.type === 'longtext'}
          onCommit={onChange}
          placeholder="—"
        />
      );
  }
}

/** 숫자 필드 — 중간 입력('-','1e')으로 NaN을 저장하지 않도록 finite일 때만 commit */
function NumberField({ value, onChange }: { value: unknown; onChange: (v: unknown) => void }) {
  const [local, setLocal] = useState(value == null || value === '' ? '' : String(value));
  useEffect(() => {
    setLocal(value == null || value === '' ? '' : String(value));
  }, [value]);
  return (
    <Input
      type="number"
      step="0.01"
      className="h-8"
      value={local}
      onChange={(e) => {
        const s = e.target.value;
        setLocal(s);
        if (s === '') onChange(undefined);
        else {
          const n = Number(s);
          if (Number.isFinite(n)) onChange(n);
        }
      }}
      onBlur={() => {
        if (local !== '' && !Number.isFinite(Number(local))) {
          setLocal(value == null ? '' : String(value)); // 불완전 입력 되돌림
        }
      }}
    />
  );
}

function EntityChip({ id, onRemove }: { id: string; onRemove?: () => void }) {
  const e = useCodexStore((s) => s.entities[id]);
  if (!e) return <Badge variant="outline">알 수 없음</Badge>;
  const Icon = ENTITY_META[e.type].icon;
  return (
    <Badge variant="default" className="gap-1 normal-case tracking-normal">
      <Icon className="size-3" style={{ color: entityColor(e) }} />
      {e.name}
      {onRemove && (
        <button type="button" onClick={onRemove} className="text-fainter hover:text-danger">
          <X className="size-3" />
        </button>
      )}
    </Badge>
  );
}

function EntityRefField({ value, onChange }: { value?: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      {value ? <EntityChip id={value} onRemove={() => onChange('')} /> : null}
      <EntityPicker
        value={value}
        onSelect={onChange}
        trigger={
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-sm border border-dashed border-line-2 px-2 py-0.5 text-xs text-faint transition hover:border-star hover:text-star"
          >
            <Plus className="size-3" /> {value ? '변경' : '선택'}
          </button>
        }
      />
    </div>
  );
}

function EntityMultiField({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {value.map((id) => (
        <EntityChip key={id} id={id} onRemove={() => onChange(value.filter((x) => x !== id))} />
      ))}
      <EntityPicker
        exclude={value}
        onSelect={(id) => onChange([...value, id])}
        trigger={
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-sm border border-dashed border-line-2 px-2 py-0.5 text-xs text-faint transition hover:border-star hover:text-star"
          >
            <Plus className="size-3" /> 추가
          </button>
        }
      />
    </div>
  );
}
